import type { MyCompetition } from '../../api/teams'

/**
 * One row per COMPETITION, not per (competition, team).
 *
 * The endpoint returns a row for every team with a stake, which is the honest
 * shape for a player — they are in a season once, with one team. For a club
 * owner fielding eight teams it produced the same competition eight times over:
 * 170 rows across 21 competitions in the seed data. The competition is the
 * subject of the row; the teams are an attribute of it.
 */

export interface CompetitionGroup {
  competition: MyCompetition
  /** Every team of yours entered in it, in the order returned. */
  teams: MyCompetition[]
  /** Strongest claim across those teams. Playing beats fielding. */
  reason: MyCompetition['reason']
}

export function groupCompetitions(rows: MyCompetition[]): CompetitionGroup[] {
  const groups = new Map<string, CompetitionGroup>()

  for (const row of rows) {
    const existing = groups.get(row.season_id)
    if (!existing) {
      groups.set(row.season_id, { competition: row, teams: [row], reason: row.reason })
      continue
    }
    existing.teams.push(row)
    // Being on the roster outranks merely fielding the team, so a competition
    // you both play in and field reads as "playing".
    if (row.reason === 'playing') {
      existing.reason = 'playing'
      existing.competition = row
    }
  }

  return [...groups.values()]
}

/** "Wolves Alpha" for one team, "3 teams" for several. */
export function teamsLabel(group: CompetitionGroup): string {
  return group.teams.length === 1
    ? group.teams[0].team_name
    : `${group.teams.length} teams`
}
